import { isNil } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinderVehiclesListDataFragment } from '../../../api/fragments/FinderVehiclesListData';
import { BooleanWithEmpty, FinderVehicleFunctionality, FinderVehicleStatus } from '../../../api/types';

const useFinderMapper = () => {
    const { t } = useTranslation(['common']);

    return useMemo(() => {
        const yesNoLabel = (value: boolean) => (value ? t('common:options.yesNo.yes') : t('common:options.yesNo.no'));

        const yesNoNullLabel = (value: boolean | null) => {
            if (isNil(value)) {
                return t('common:options.empty');
            }

            return yesNoLabel(value);
        };

        const booleanWithEmptyToBoolean = (value: BooleanWithEmpty | null) => {
            switch (value) {
                case BooleanWithEmpty.On:
                    return true;

                case BooleanWithEmpty.Off:
                    return false;

                default:
                    return null;
            }
        };

        const stateLabel = (isStateHidden: boolean) =>
            !isStateHidden
                ? t('common:options.finderVehicle.state.show')
                : t('common:options.finderVehicle.state.hide');

        const getIsPreowned = (finder?: FinderVehiclesListDataFragment): boolean => {
            if (!finder) {
                return false;
            }

            if (finder.module.__typename !== 'FinderVehicleManagementModule') {
                throw new Error('Unexpected module type for Finder Vehicle');
            }

            const condition = finder.listing.vehicle.condition.value?.toLowerCase();

            return condition !== 'new';
        };

        const inspectedLabel = (finder?: FinderVehiclesListDataFragment) =>
            getIsPreowned(finder) ? yesNoLabel(finder.setting.isInspected) : t('common:options.empty');

        const ltaLabel = (finder?: FinderVehiclesListDataFragment) =>
            getIsPreowned(finder) ? yesNoLabel(!!finder.lta) : t('common:options.empty');

        const conditionLabel = (finder?: FinderVehiclesListDataFragment) => {
            if (!finder) {
                return '';
            }

            const condition = finder.listing.vehicle.condition.value?.toLowerCase();
            const porscheApproved = finder.listing.warranty?.porscheApproved;

            if (condition === 'new') {
                return t('common:options.finderVehicle.condition.new');
            }

            if (condition === 'preowned' && porscheApproved) {
                return t('common:options.finderVehicle.condition.porscheApproved');
            }

            return t('common:options.finderVehicle.condition.preowned');
        };

        const statusLabel = (status: FinderVehicleStatus) => {
            switch (status) {
                case FinderVehicleStatus.Available:
                    return t('common:options.finderVehicle.status.available');

                case FinderVehicleStatus.Reserved:
                    return t('common:options.finderVehicle.status.reserved');

                case FinderVehicleStatus.Submitted:
                    return t('common:options.finderVehicle.status.submitted');

                default:
                    throw new Error('Unknown status');
            }
        };

        const functionalityLabel = (functionality: FinderVehicleFunctionality) => {
            switch (functionality) {
                case FinderVehicleFunctionality.OneTime:
                    return t('common:options.finderVehicle.functionality.oneTime');

                case FinderVehicleFunctionality.Permanent:
                    return t('common:options.finderVehicle.functionality.permanent');

                default:
                    throw new Error('Unknown functionality');
            }
        };

        return {
            booleanWithEmptyToBoolean,
            yesNoLabel,
            yesNoNullLabel,
            inspectedLabel,
            conditionLabel,
            ltaLabel,
            statusLabel,
            functionalityLabel,
            stateLabel,
        };
    }, [t]);
};

export default useFinderMapper;
